import { useEffect, useState, lazy, Suspense } from "react";
import { Link } from "react-router-dom";
import { applyPageSeo } from "../lib/seo";
import { BrandLogo } from "../components/BrandLogo";

const FeaturedTemplates = lazy(() =>
  import("../components/FeaturedTemplates").then((m) => ({ default: m.FeaturedTemplates }))
);

const HeroCarousel = lazy(() =>
  import("../components/HeroCarousel").then((m) => ({ default: m.HeroCarousel }))
);

const faqItems = [
  {
    q: "What is React Email?",
    a: "An open-source library for building emails with React components. Our builder generates React Email code and renders it to HTML for any email provider.",
  },
  {
    q: "Do I need to know React?",
    a: "No. Describe what you want in plain English and the AI handles the code. Export HTML (works everywhere) or React source if you want to customise further.",
  },
  {
    q: "Can I use exported emails with any provider?",
    a: "Yes. Export as HTML and use with any ESP — Resend, SendGrid, Mailchimp, Postmark, AWS SES, or others.",
  },
  {
    q: "Is the free tier actually free?",
    a: "Yes. No credit card required. 3 AI generations/day, 5 exports/month, 10 saved drafts. A Pro plan with higher limits and React component export is coming soon.",
  },
  {
    q: "How does AI generation work?",
    a: "Describe the email you need. AI generates React Email JSX with a live preview. Iterate on the prompt, then export.",
  },
  {
    q: "Are brand templates free to use?",
    a: "All 148 templates are free to browse and preview. HTML export is free; React source export is a Pro feature. They're inspired by real patterns — starting points you can customise.",
  },
];

const proofMetrics = [
  { value: "148+", label: "Templates" },
  { value: "\u003c30s", label: "Preview" },
  { value: "HTML + React", label: "Export" },
  { value: "Free", label: "Start" },
];

const featureHighlights = [
  {
    title: "Prompt-to-preview",
    description: "Describe, get a draft instantly.",
    icon: "ai",
  },
  {
    title: "Visual editing",
    description: "Tweak without touching code.",
    icon: "edit",
  },
  {
    title: "Template gallery",
    description: "Proven patterns — receipts, promos, alerts.",
    icon: "gallery",
  },
  {
    title: "Export anywhere",
    description: "HTML or React, any ESP.",
    icon: "export",
  },
] as const;

function FeatureIcon({ type }: { type: string }) {
  switch (type) {
    case "ai":
      return (
        <svg viewBox="0 0 24 24" fill="none" aria-hidden="true" className="h-6 w-6">
          <path d="M12 2L14.09 8.26L20 9.27L15.55 13.97L16.91 20L12 16.9L7.09 20L8.45 13.97L4 9.27L9.91 8.26L12 2Z" fill="#7dd3fc" stroke="#0284c7" strokeWidth="1.5" strokeLinejoin="round" />
        </svg>
      );
    case "edit":
      return (
        <svg viewBox="0 0 24 24" fill="none" aria-hidden="true" className="h-6 w-6">
          <path d="M11 4H4a2 2 0 00-2 2v14a2 2 0 002 2h14a2 2 0 002-2v-7" stroke="#f59e0b" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
          <path d="M18.5 2.5a2.121 2.121 0 013 3L12 15l-4 1 1-4 9.5-9.5z" fill="#fde68a" stroke="#f59e0b" strokeWidth="1.5" strokeLinejoin="round" />
        </svg>
      );
    case "gallery":
      return (
        <svg viewBox="0 0 24 24" fill="none" aria-hidden="true" className="h-6 w-6">
          <rect x="3" y="3" width="7" height="7" rx="1.5" fill="#a5f3fc" stroke="#06b6d4" strokeWidth="1.5" />
          <rect x="14" y="3" width="7" height="7" rx="1.5" fill="#a5f3fc" stroke="#06b6d4" strokeWidth="1.5" />
          <rect x="3" y="14" width="7" height="7" rx="1.5" fill="#a5f3fc" stroke="#06b6d4" strokeWidth="1.5" />
          <rect x="14" y="14" width="7" height="7" rx="1.5" fill="#a5f3fc" stroke="#06b6d4" strokeWidth="1.5" />
        </svg>
      );
    case "export":
      return (
        <svg viewBox="0 0 24 24" fill="none" aria-hidden="true" className="h-6 w-6">
          <path d="M21 15v4a2 2 0 01-2 2H5a2 2 0 01-2-2v-4" stroke="#10b981" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
          <path d="M7 10l5 5 5-5" stroke="#10b981" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
          <path d="M12 15V3" stroke="#10b981" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
      );
    default:
      return null;
  }
}

const workflowSteps = [
  {
    step: "01",
    title: "Describe",
    description: "Goal, audience, tone.",
  },
  {
    step: "02",
    title: "Refine",
    description: "Tweak layout, no code.",
  },
  {
    step: "03",
    title: "Export",
    description: "HTML or React. Ship.",
  },
];

const brandPatterns = [
  "Airbnb", "Stripe", "Vercel", "Slack", "Notion", "GitHub",
  "Netflix", "Spotify", "Amazon", "DHL", "PayPal", "Figma",
  "Uber", "Zoom", "Nike", "Shopify", "Canva", "Grammarly",
];

const trustStack = [
  "React Email", "Gemini", "Better Auth", "Stripe", "Cloudflare", "Tailwind CSS",
];

function ArrowIcon() {
  return (
    <svg aria-hidden="true" viewBox="0 0 20 20" fill="none" className="h-5 w-5">
      <path d="M4.166 10h11.667m0 0-4.167-4.167M15.833 10l-4.167 4.167" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

function CheckIcon({ className = "h-5 w-5" }: { className?: string } = {}) {
  return (
    <svg aria-hidden="true" viewBox="0 0 20 20" fill="none" className={className}>
      <path d="m5 10 3.2 3.2L15 6.5" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

function ChevronIcon({ open }: { open: boolean }) {
  return (
    <svg
      aria-hidden="true"
      viewBox="0 0 20 20"
      fill="none"
      className={`h-5 w-5 text-slate-400 transition-transform duration-200 ${open ? "rotate-180" : ""}`}
    >
      <path d="M5 7.5l5 5 5-5" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

function FaqAccordion() {
  const [openIndex, setOpenIndex] = useState<number | null>(null);

  return (
    <dl className="mt-12 divide-y divide-slate-200">
      {faqItems.map((item, i) => (
        <div key={item.q} className="py-5">
          <dt>
            <button
              type="button"
              onClick={() => setOpenIndex(openIndex === i ? null : i)}
              className="flex w-full items-center justify-between text-left text-lg font-semibold text-slate-950 transition hover:text-slate-700"
              aria-expanded={openIndex === i}
            >
              {item.q}
              <ChevronIcon open={openIndex === i} />
            </button>
          </dt>
          <dd
            className={`grid transition-all duration-200 ease-in-out ${openIndex === i ? "grid-rows-[1fr] opacity-100" : "grid-rows-[0fr] opacity-0"}`}
          >
            <div className="overflow-hidden">
              <p className="pt-3 text-base leading-7 text-slate-600">{item.a}</p>
            </div>
          </dd>
        </div>
      ))}
    </dl>
  );
}


export function Home() {
  const [checkoutEnabled, setCheckoutEnabled] = useState(false);

  useEffect(() => {
    let cancelled = false;
    void (async () => {
      try {
        const res = await fetch("/api/billing/config");
        if (res.ok && !cancelled) {
          const config = (await res.json()) as { checkoutEnabled?: boolean };
          setCheckoutEnabled(Boolean(config.checkoutEnabled));
        }
      } catch {
        // leave false — checkout assumed unavailable
      }
    })();
    return () => { cancelled = true; };
  }, []);

  useEffect(() => {
    const cleanup = applyPageSeo({
      title: "148+ AI Email Templates for Developers",
      description:
        "Generate polished lifecycle, transactional, and promotional emails with AI. Browse 148+ templates across 75 brands. Export production-ready HTML and React Email code. Start free.",
      path: "/",
      keywords: ["react email builder", "email template generator", "AI email builder", "email template", "react email", "react email templates", "email builder", "AI email generator"],
    });

    // Add FAQ structured data for Google rich results
    const faqJsonLd = {
      "@context": "https://schema.org",
      "@type": "FAQPage",
      "mainEntity": faqItems.map((item) => ({
        "@type": "Question",
        "name": item.q,
        "acceptedAnswer": {
          "@type": "Answer",
          "text": item.a,
        },
      })),
    };
    const script = document.createElement("script");
    script.type = "application/ld+json";
    script.textContent = JSON.stringify(faqJsonLd);
    script.id = "faq-jsonld";
    document.head.appendChild(script);

    return () => {
      cleanup();
      const existing = document.getElementById("faq-jsonld");
      if (existing) existing.remove();
    };
  }, []);

  return (
    <div className="bg-white text-slate-950">
      {/* Hero */}
      <section className="app-surface relative overflow-hidden border-b border-slate-200/70">
        <div className="absolute inset-x-0 top-0 h-px bg-gradient-to-r from-transparent via-sky-300/80 to-transparent" />
        <div aria-hidden="true" className="absolute inset-0 bg-grid-fade opacity-60" />
        <div aria-hidden="true" className="absolute -left-32 top-24 h-80 w-80 rounded-full bg-sky-300/25 blur-[120px]" />
        <div aria-hidden="true" className="absolute -right-24 -top-12 h-72 w-72 rounded-full bg-indigo-300/20 blur-[120px]" />

        <div className="relative mx-auto max-w-7xl px-4 pb-16 pt-16 sm:px-6 sm:pb-20 sm:pt-24 lg:px-8 lg:pb-24">
          <div className="grid items-center gap-12 lg:grid-cols-[1.05fr_0.95fr]">
            <div className="max-w-2xl animate-hero-fade">
              <div className="inline-flex items-center gap-2 rounded-full border border-sky-200/80 bg-white/80 px-3 py-1 text-xs font-medium text-slate-700 shadow-sm shadow-sky-100/60 backdrop-blur">
                <span className="relative flex h-1.5 w-1.5">
                  <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-emerald-400 opacity-75" />
                  <span className="relative inline-flex h-1.5 w-1.5 rounded-full bg-emerald-500" />
                </span>
                148+ templates · live
              </div>

              <h1 className="mt-6 text-[2.5rem] font-semibold tracking-tight text-balance leading-[1.05] text-slate-950 sm:text-5xl lg:text-[3.75rem]">
                Ship emails in <span className="bg-gradient-to-br from-sky-600 via-sky-500 to-indigo-500 bg-clip-text text-transparent">minutes</span>.
              </h1>

              <p className="mt-4 max-w-xl text-base leading-7 text-slate-600 sm:text-lg">
                Describe what you need. Get a polished React Email draft. Export production-ready HTML.
              </p>

              <div className="mt-7 flex flex-col items-start gap-3 sm:flex-row sm:items-center">
                <Link
                  to="/auth/signup?redirect=%2Fbuilder"
                  className="group inline-flex items-center gap-2 rounded-md bg-slate-950 px-5 py-2.5 text-sm font-semibold text-white shadow-[0_12px_30px_-12px_rgba(15,23,42,0.6)] ring-1 ring-slate-900/10 transition hover:bg-slate-800 hover:shadow-[0_18px_40px_-12px_rgba(15,23,42,0.7)]"
                >
                  Create free account
                  <span className="transition-transform duration-200 group-hover:translate-x-0.5"><ArrowIcon /></span>
                </Link>
                <Link
                  to="/discover/templates"
                  className="inline-flex items-center gap-2 rounded-md border border-slate-300/80 bg-white/90 px-5 py-2.5 text-sm font-semibold text-slate-700 backdrop-blur transition hover:border-slate-400 hover:bg-white"
                >
                  Browse templates
                </Link>
              </div>

              <div className="mt-4 flex flex-wrap items-center gap-x-3 gap-y-1 text-xs text-slate-500">
                <span className="inline-flex items-center gap-1.5">
                  <CheckIcon className="h-3.5 w-3.5 text-emerald-500" /> Free plan
                </span>
                <span className="text-slate-300">·</span>
                <span className="inline-flex items-center gap-1.5"><CheckIcon className="h-3.5 w-3.5 text-emerald-500" /> No card needed</span>
                <span className="text-slate-300">·</span>
                {checkoutEnabled ? (
                  <span className="inline-flex items-center gap-1.5"><CheckIcon className="h-3.5 w-3.5 text-emerald-500" /> Cancel anytime</span>
                ) : (
                  <span className="inline-flex items-center gap-1.5"><CheckIcon className="h-3.5 w-3.5 text-emerald-500" /> No commitment</span>
                )}
              </div>
            </div>

            {/* Rotating template carousel */}
            <div className="relative">
              <div className="absolute -left-6 top-10 hidden h-28 w-28 rounded-full bg-amber-200/45 blur-2xl lg:block" />
              <Suspense fallback={<div className="flex h-[380px] items-center justify-center rounded border border-slate-200 bg-white shadow-[0_30px_90px_-40px_rgba(15,23,42,0.45)]"><div className="h-8 w-8 animate-spin rounded-full border-4 border-slate-200 border-t-sky-600" /></div>}>
                <HeroCarousel />
              </Suspense>
            </div>
          </div>

          {/* Proof metrics */}
          <div className="mt-14 grid gap-3 sm:grid-cols-2 md:grid-cols-4">
            {proofMetrics.map((metric) => (
              <div
                key={metric.label}
                className="group relative overflow-hidden rounded-lg border border-slate-200/70 bg-white/85 px-4 py-3.5 shadow-soft backdrop-blur transition hover:-translate-y-0.5 hover:border-slate-300 hover:shadow-card"
              >
                <p className="text-2xl font-semibold tracking-tight text-slate-950">
                  {metric.value}
                </p>
                <p className="mt-1 text-xs font-medium uppercase tracking-wider text-slate-500">
                  {metric.label}
                </p>
                <span aria-hidden="true" className="absolute inset-x-0 bottom-0 h-px bg-gradient-to-r from-transparent via-sky-400/40 to-transparent opacity-0 transition-opacity group-hover:opacity-100" />
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Feature highlights */}
      <section className="relative bg-white py-14 sm:py-16">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="max-w-2xl">
            <p className="eyebrow">Features</p>
            <h2 className="mt-3 text-2xl font-semibold tracking-tight text-slate-950 sm:text-3xl">
              Built to ship emails faster.
            </h2>
            <p className="mt-2 text-sm leading-6 text-slate-500 sm:text-base">
              A focused workflow — describe, preview, export. No table HTML, no client-prefix juggling.
            </p>
          </div>

          <div className="mt-10 grid gap-3 lg:grid-cols-2 xl:grid-cols-4">
            {featureHighlights.map((feature) => (
              <div
                key={feature.title}
                className="group relative overflow-hidden rounded-lg border border-slate-200/70 bg-white p-4 shadow-soft transition hover:-translate-y-0.5 hover:border-slate-300 hover:shadow-card"
              >
                <span aria-hidden="true" className="absolute -right-10 -top-10 h-24 w-24 rounded-full bg-sky-100/0 blur-2xl transition-colors group-hover:bg-sky-100/70" />
                <div className="relative flex h-10 w-10 items-center justify-center rounded-md bg-gradient-to-br from-white to-slate-50 shadow-sm ring-1 ring-slate-200/80">
                  <FeatureIcon type={feature.icon} />
                </div>
                <h3 className="relative mt-4 text-base font-semibold text-slate-950">
                  {feature.title}
                </h3>
                <p className="relative mt-1 text-sm leading-5 text-slate-500">
                  {feature.description}
                </p>
              </div>
            ))}
          </div>

          <div className="mt-12 flex justify-center">
            <Link
              to="/auth/signup?redirect=%2Fbuilder"
              className="group inline-flex items-center gap-2 rounded-md bg-slate-950 px-5 py-2.5 text-sm font-semibold text-white shadow-[0_12px_30px_-12px_rgba(15,23,42,0.6)] transition hover:bg-slate-800 hover:shadow-[0_18px_40px_-12px_rgba(15,23,42,0.7)]"
            >
              Create free account
              <span className="transition-transform duration-200 group-hover:translate-x-0.5"><ArrowIcon /></span>
            </Link>
          </div>
        </div>
      </section>

      {/* Workflow — dark section */}
      <section className="dark-surface relative overflow-hidden border-y border-slate-900/10 py-16 text-white sm:py-20">
        <div aria-hidden="true" className="absolute inset-0 bg-grid-fade opacity-[0.12]" />
        <div aria-hidden="true" className="absolute -left-32 top-0 h-72 w-72 rounded-full bg-sky-500/15 blur-[120px]" />
        <div aria-hidden="true" className="absolute -right-24 bottom-0 h-72 w-72 rounded-full bg-indigo-500/15 blur-[120px]" />

        <div className="relative mx-auto grid max-w-7xl gap-12 px-4 sm:px-6 lg:grid-cols-[0.9fr_1.1fr] lg:items-center lg:px-8">
          <div>
            <p className="text-xs font-semibold uppercase tracking-[0.24em] text-sky-300">
              Prompt to production
            </p>
            <h2 className="mt-3 text-2xl font-semibold tracking-tight sm:text-3xl lg:text-4xl">
              Generate. Edit. <span className="bg-gradient-to-br from-sky-300 to-cyan-200 bg-clip-text text-transparent">Export.</span>
            </h2>
            <p className="mt-3 max-w-md text-sm leading-6 text-slate-400">
              A streamlined loop from rough idea to provider-ready HTML — without ever opening a table tag.
            </p>

            <div className="mt-8 flex flex-wrap gap-2">
              {trustStack.map((item) => (
                <span
                  key={item}
                  className="rounded-md border border-white/10 bg-white/5 px-3 py-1.5 text-xs font-medium text-slate-300 backdrop-blur"
                >
                  {item}
                </span>
              ))}
            </div>
          </div>

          <div className="relative space-y-3">
            <div aria-hidden="true" className="absolute left-[1.65rem] top-3 bottom-3 w-px bg-gradient-to-b from-sky-400/0 via-sky-400/40 to-sky-400/0" />
            {workflowSteps.map((step) => (
              <div
                key={step.step}
                className="group relative rounded-lg border border-white/10 bg-white/5 px-4 py-3.5 backdrop-blur transition hover:border-white/20 hover:bg-white/[0.07]"
              >
                <div className="flex items-center gap-4">
                  <div className="relative flex h-9 w-9 shrink-0 items-center justify-center rounded-md bg-gradient-to-br from-sky-300 to-sky-500 text-xs font-bold text-slate-950 shadow-[0_8px_24px_-8px_rgba(56,189,248,0.6)]">
                    {step.step}
                  </div>
                  <div>
                    <h3 className="text-base font-semibold text-white">{step.title}</h3>
                    <p className="mt-0.5 text-sm text-slate-400">{step.description}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Brand library */}
      <section className="relative overflow-hidden bg-gradient-to-b from-slate-50 to-white py-14 sm:py-16">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="grid gap-10 lg:grid-cols-[0.9fr_1.1fr] lg:items-center">
            <div>
              <p className="eyebrow">Brand library</p>
              <h2 className="mt-3 text-2xl font-semibold tracking-tight text-slate-950 sm:text-3xl">
                148 templates. <span className="text-slate-500">75 brands.</span>
              </h2>
              <p className="mt-3 max-w-md text-sm leading-6 text-slate-600">
                Receipts, alerts, promos, onboarding — modelled on real brand patterns. Customise and ship in minutes.
              </p>
              <Link
                to="/discover/templates"
                className="mt-5 inline-flex items-center gap-2 text-sm font-semibold text-sky-700 transition hover:text-sky-600"
              >
                Browse the gallery
                <ArrowIcon />
              </Link>
            </div>

            <div className="rounded-xl border border-slate-200/70 bg-white p-4 shadow-card sm:p-5">
              <div className="grid grid-cols-3 gap-2 sm:grid-cols-6">
                {brandPatterns.map((brand) => (
                  <div
                    key={brand}
                    className="group flex flex-col items-center gap-2 rounded-md border border-transparent bg-slate-50/60 px-2 py-3 transition hover:-translate-y-0.5 hover:border-slate-200 hover:bg-white hover:shadow-soft"
                  >
                    <BrandLogo brand={brand} className="h-8 w-8 rounded-md transition group-hover:scale-105" />
                    <span className="text-[11px] font-medium text-slate-600 transition group-hover:text-slate-900">{brand}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      <Suspense fallback={<div className="bg-white py-14"><div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 text-center"><div className="mx-auto h-8 w-8 animate-spin rounded-full border-4 border-slate-200 border-t-sky-600" /></div></div>}>
        <FeaturedTemplates />
      </Suspense>

      {/* Developer fit */}
      <section className="bg-white py-14 sm:py-16">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="grid gap-10 lg:grid-cols-[0.8fr_1.2fr] lg:items-start">
            <div>
              <p className="eyebrow">Developer-ready</p>
              <h2 className="mt-3 text-2xl font-semibold tracking-tight text-slate-950 sm:text-3xl">
                React Email in. <br className="hidden sm:inline" />HTML out.
              </h2>
              <p className="mt-3 max-w-sm text-sm leading-6 text-slate-600">
                Export clean, client-safe templates for any email stack. JSX source available for codebase integration.
              </p>
              <Link
                to="/auth/signup?redirect=%2Fbuilder"
                className="group mt-6 inline-flex items-center gap-2 rounded-md bg-slate-950 px-5 py-2.5 text-sm font-semibold text-white shadow-[0_12px_30px_-12px_rgba(15,23,42,0.6)] transition hover:bg-slate-800 hover:shadow-[0_18px_40px_-12px_rgba(15,23,42,0.7)]"
              >
                Create free account
                <span className="transition-transform duration-200 group-hover:translate-x-0.5"><ArrowIcon /></span>
              </Link>
            </div>

            <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
              <div className="flex items-start gap-3 rounded-lg border border-slate-200/70 bg-slate-50/60 p-3.5 transition hover:-translate-y-0.5 hover:border-slate-300 hover:bg-white hover:shadow-soft">
                <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-md bg-rose-100">
                  <svg viewBox="0 0 24 24" fill="none" aria-hidden="true" className="h-4 w-4 text-rose-600">
                    <path d="M6 18L18 6M6 6l12 12" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
                  </svg>
                </div>
                <div>
                  <h3 className="text-sm font-semibold text-slate-950">No table wrestling</h3>
                  <p className="mt-0.5 text-xs text-slate-500">React components.</p>
                </div>
              </div>

              <div className="flex items-start gap-3 rounded-lg border border-slate-200/70 bg-slate-50/60 p-3.5 transition hover:-translate-y-0.5 hover:border-slate-300 hover:bg-white hover:shadow-soft">
                <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-md bg-amber-100">
                  <svg viewBox="0 0 24 24" fill="none" aria-hidden="true" className="h-4 w-4 text-amber-600">
                    <path d="M13 10V3L4 14h7v7l9-11h-7z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                  </svg>
                </div>
                <div>
                  <h3 className="text-sm font-semibold text-slate-950">AI first draft</h3>
                  <p className="mt-0.5 text-xs text-slate-500">Prompt → preview.</p>
                </div>
              </div>

              <div className="flex items-start gap-3 rounded-lg border border-slate-200/70 bg-slate-50/60 p-3.5 transition hover:-translate-y-0.5 hover:border-slate-300 hover:bg-white hover:shadow-soft">
                <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-md bg-emerald-100">
                  <svg viewBox="0 0 24 24" fill="none" aria-hidden="true" className="h-4 w-4 text-emerald-600">
                    <path d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                  </svg>
                </div>
                <div>
                  <h3 className="text-sm font-semibold text-slate-950">Export anywhere</h3>
                  <p className="mt-0.5 text-xs text-slate-500">HTML or React.</p>
                </div>
              </div>

              <div className="flex items-start gap-3 rounded border border-slate-200 bg-white p-3">
                <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-md bg-sky-100">
                  <svg viewBox="0 0 24 24" fill="none" aria-hidden="true" className="h-4 w-4 text-sky-600">
                    <path d="M3 8l9-5 9 5M3 8v8l9 5M3 8l9 5m0 0l9-5m-9 5v8" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                  </svg>
                </div>
                <div>
                  <h3 className="text-sm font-semibold text-slate-950">React Email</h3>
                  <p className="mt-0.5 text-xs text-slate-500">Native JSX.</p>
                </div>
              </div>

              <div className="flex items-start gap-3 rounded border border-slate-200 bg-white p-3">
                <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-md bg-emerald-100">
                  <svg viewBox="0 0 24 24" fill="none" aria-hidden="true" className="h-4 w-4 text-emerald-600">
                    <path d="M21 12a9 9 0 01-9 9m9-9a9 9 0 00-9-9m9 9H3m9 9a9 9 0 01-9-9m9 9c1.66 0 3-4 3-9s-1.34-9-3-9m0 18c-1.66 0-3-4-3-9s1.34-9 3-9" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                  </svg>
                </div>
                <div>
                  <h3 className="text-sm font-semibold text-slate-950">Any ESP</h3>
                  <p className="mt-0.5 text-xs text-slate-500">Resend, SES, SendGrid.</p>
                </div>
              </div>

              <div className="flex items-start gap-3 rounded border border-slate-200 bg-white p-3">
                <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-md bg-amber-100">
                  <svg viewBox="0 0 24 24" fill="none" aria-hidden="true" className="h-4 w-4 text-amber-600">
                    <path d="M5 12h14M12 5l7 7-7 7" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                  </svg>
                </div>
                <div>
                  <h3 className="text-sm font-semibold text-slate-950">Client-safe HTML</h3>
                  <p className="mt-0.5 text-xs text-slate-500">Ready to send.</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* FAQ — accordion */}
      <section className="bg-slate-50 py-8 sm:py-10">
        <div className="mx-auto max-w-3xl px-4 sm:px-6 lg:px-8">
          <div className="text-center">
            <p className="text-xs font-semibold uppercase tracking-[0.24em] text-sky-700">
              FAQ
            </p>
            <h2 className="mt-3 text-xl font-semibold tracking-tight text-slate-950 sm:text-2xl">
              Common questions
            </h2>
          </div>

          <FaqAccordion />

          <div className="mt-10 text-center">
            <Link
              to="/pricing"
              className="inline-flex items-center gap-2 text-base font-semibold text-sky-700 transition hover:text-sky-600"
            >
              View pricing
              <ArrowIcon />
            </Link>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="bg-white py-8 sm:py-10">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="overflow-hidden rounded-md bg-[linear-gradient(135deg,#0f172a_0%,#111827_52%,#0c4a6e_100%)] px-5 py-5 text-white shadow-[0_24px_60px_-30px_rgba(2,6,23,0.7)] sm:px-6 lg:px-8">
            <div className="grid items-center gap-8 lg:grid-cols-[1fr_auto]">
              <div className="max-w-2xl">
                <h2 className="text-xl font-semibold tracking-tight sm:text-2xl">
                  Build your first email now.
                </h2>
                <p className="mt-1.5 text-xs text-slate-400">
                  {checkoutEnabled ? "No credit card. Cancel anytime." : "No credit card required."}
                </p>
              </div>

              <div className="flex flex-col gap-3">
                <Link
                  to="/auth/signup?redirect=%2Fbuilder"
                  className="inline-flex items-center justify-center gap-2 rounded-md bg-white px-5 py-2.5 text-sm font-semibold text-slate-950 transition hover:bg-slate-100"
                >
                  Create account
                  <ArrowIcon />
                </Link>
                <Link
                  to="/pricing"
                  className="inline-flex items-center justify-center rounded-md border border-white/30 bg-white/10 px-5 py-2.5 text-sm font-semibold text-white transition hover:border-white/50 hover:bg-white/15"
                >
                  See pricing
                </Link>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}